import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Users, TrendingUp, Activity, RefreshCw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import type { TenantStats } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

export default function SuperAdminDashboard() {
  const { t } = useTranslation(["superadmin2", "common"]);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { data: tenantStats = [], isLoading } = useQuery({
    queryKey: ['tenant-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_stats_cache' as any)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as TenantStats[];
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('refresh-tenant-stats');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-stats'] });
      toast({
        title: t("dashboard.refreshSuccessTitle"),
        description: t("dashboard.refreshSuccessDesc"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("dashboard.errorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const totalTenants = tenantStats.length;
  const totalUsers = tenantStats.reduce((sum, t) => sum + t.total_users, 0);
  const totalRevenue = tenantStats.reduce((sum, t) => sum + Number(t.revenue_last_30d), 0);
  const totalBookings = tenantStats.reduce((sum, t) => sum + t.total_bookings_last_30d, 0);

  return (
    <SuperAdminLayout>
      <div className="space-y-3 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
              {isMobile ? "Dashboard" : "Super Admin Dashboard"}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">{t("dashboard.subtitle")}</p>
          </div>
          <Button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            size={isMobile ? "icon" : "default"}
          >
            <RefreshCw className={`h-4 w-4 ${isMobile ? '' : 'mr-2'} ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            {!isMobile && t("dashboard.refresh")}
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                {t("dashboard.totalTenants")}
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              <div className="text-xl md:text-2xl font-bold text-foreground">{totalTenants}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                {t("dashboard.totalUsers")}
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              <div className="text-xl md:text-2xl font-bold text-foreground">{totalUsers}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                {t("dashboard.revenue30d")}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              <div className="text-xl md:text-2xl font-bold text-foreground">
                {totalRevenue.toFixed(2)} DT
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                {t("dashboard.bookings30d")}
              </CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              <div className="text-xl md:text-2xl font-bold text-foreground">{totalBookings}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="p-3 md:p-6">
            <CardTitle className="text-foreground text-base md:text-lg">{t("dashboard.recentTenants")}</CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-6 pt-0">
            {isLoading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {tenantStats.slice(0, isMobile ? 6 : 6).map((tenant) => (
                  <div
                    key={tenant.gym_id}
                    className="flex flex-col p-3 rounded-lg bg-muted border border-border space-y-2"
                  >
                    <div>
                      <p className="font-medium text-foreground text-sm md:text-base">{tenant.gym_name}</p>
                      <p className="text-xs text-muted-foreground">{tenant.city}</p>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{tenant.total_users} users</span>
                      <span>{tenant.active_subscriptions} subs</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
